<template>
  <el-dialog :title="title" :visible="visible" width="480px" append-to-body @update:visible="$emit('update:visible', $event)" @closed="reset">
    <div class="text-center" v-loading="preparing">
      <img v-if="previewUrl" :src="previewUrl" class="avatar-preview" alt="New photo" />
      <img v-else-if="current" :src="current" class="avatar-preview" alt="Current photo" />
      <p v-else class="text-muted my-4">No photo yet.</p>

      <input ref="avatarInput" type="file" :accept="acceptedTypes" class="d-none" @change="onPicked" />
      <div class="mt-3">
        <el-button v-if="canManageEmployees" icon="el-icon-upload2" @click="$refs.avatarInput.click()">Choose a photo</el-button>
      </div>
      <small class="text-muted d-block mt-2">
        Cut to a centred square of {{ avatarSize }}×{{ avatarSize }} px — JPEG, PNG or WebP, up to 5 MB.
      </small>
      <el-progress v-if="uploadProgress" :percentage="uploadProgress" class="mt-3" />
    </div>

    <span slot="footer" class="d-flex">
      <el-button v-if="canManageEmployees && current && !previewUrl" type="danger" plain :loading="saving" @click="remove">Remove photo</el-button>
      <span class="ml-auto">
        <el-button @click="$emit('update:visible', false)">Cancel</el-button>
        <el-button v-if="canManageEmployees" type="primary" :disabled="!blob" :loading="saving" @click="save">Save</el-button>
      </span>
    </span>
  </el-dialog>
</template>

<script>
import employeeAccess from "@/components/Employees/employeeAccess";
import APIEmployees from "@/api/modules/employees";
import { resultErrorMessage, resultErrorOf } from "@/api/helpers/resultError";
import { ACCEPTED_TYPES, AVATAR_SIZE, MAX_BYTES, buildAvatar } from "./avatarImage";

/**
 * The employee photo: cut to a square in the browser, uploaded through cms-api (S3) into
 * users.avatar. Used by the Employees list (click the photo) and the employee card.
 * Emits `changed` with the new URL, or null after a removal.
 */
export default {
  mixins: [employeeAccess],
  name: "EmployeeAvatarDialog",
  props: {
    visible: { type: Boolean, default: false },
    // users.id
    userId: { type: [Number, String], default: null },
    name: { type: String, default: "" },
    current: { type: String, default: null },
  },
  data() {
    return { blob: null, previewUrl: "", preparing: false, saving: false, uploadProgress: 0 };
  },
  computed: {
    title() {
      return this.name ? `Photo — ${this.name}` : "Photo";
    },
    acceptedTypes() {
      return ACCEPTED_TYPES.join(",");
    },
    avatarSize() {
      return AVATAR_SIZE;
    },
  },
  methods: {
    async onPicked(event) {
      if (!this.canManageEmployees) return;
      const [file] = event.target.files || [];
      event.target.value = "";
      if (!file) return;

      if (!ACCEPTED_TYPES.includes(file.type) || file.size > MAX_BYTES) {
        this.$notify({ type: "warning", title: "The photo must be a JPEG, PNG or WebP of at most 5 MB" });

        return;
      }

      this.preparing = true;
      try {
        this.blob = await buildAvatar(file);
        this.setPreview(URL.createObjectURL(this.blob));
      } catch (e) {
        this.notifyError(e, "Could not read the photo");
      } finally {
        this.preparing = false;
      }
    },
    async save() {
      if (!this.canManageEmployees || !(await this.ensureEmployeeWriteAccess())) return;
      if (!this.blob || !this.userId) return;

      this.saving = true;
      try {
        const response = await APIEmployees.uploadAvatar(this.userId, this.blob, (event) => {
          if (event.total) this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        });
        if (this.refused(response)) return;
        this.$emit("changed", response.data.avatar);
        this.$notify({ type: "success", title: "Photo saved" });
        this.$emit("update:visible", false);
      } catch (e) {
        this.notifyError(e, "Could not save the photo");
      } finally {
        this.saving = false;
        this.uploadProgress = 0;
      }
    },
    async remove() {
      if (!this.canManageEmployees) return;
      try {
        await this.$confirm("Remove this employee's photo?", "Warning", {
          confirmButtonText: "Remove",
          cancelButtonText: "Cancel",
          type: "warning",
        });
      } catch (e) {
        return;
      }

      if (!(await this.ensureEmployeeWriteAccess())) return;
      this.saving = true;
      try {
        const response = await APIEmployees.removeAvatar(this.userId);
        if (this.refused(response)) return;
        this.$emit("changed", null);
        this.$notify({ type: "success", title: "Photo removed" });
        this.$emit("update:visible", false);
      } catch (e) {
        this.notifyError(e, "Could not remove the photo");
      } finally {
        this.saving = false;
      }
    },
    setPreview(url) {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = url;
    },
    reset() {
      this.setPreview("");
      this.blob = null;
    },
    // cms-api answers a refusal with HTTP 200 and a Result body
    refused(response) {
      const error = resultErrorOf(response);
      if (!error) return false;

      this.$notify({ type: "warning", title: resultErrorMessage(error) });

      return true;
    },
    notifyError(error, fallback) {
      const payload = error && error.response && error.response.data;
      if (payload && payload.error) {
        this.$notify({ type: "warning", title: resultErrorMessage(payload.error) });

        return;
      }
      this.$notify({ type: "warning", title: (error && error.message) || fallback });
    },
  },
};
</script>

<style scoped>
.avatar-preview {
  width: 240px;
  height: 240px;
  object-fit: cover;
  border-radius: 50%;
}
</style>
